import { useEffect, useRef, useState } from 'react';

export interface AIBlockCredential {
  icon: string;
  label: string;
  sub: string;
}

export interface SectionAIBlockProps {
  eyebrow?: string;
  heading: string;
  headingAccent?: string;
  paragraphs: string[];
  credentials?: AIBlockCredential[];
}

const DEFAULT_CREDENTIALS: AIBlockCredential[] = [
  { icon: 'ri-medal-2-fill', label: 'GAF Certified Contractor', sub: 'Industry-recognized quality standard' },
  { icon: 'ri-award-fill', label: 'Fortification Certified', sub: 'Storm-resistant installation methods' },
  { icon: 'ri-shield-check-fill', label: 'Licensed & Insured', sub: 'Mississippi state licensed' },
  { icon: 'ri-file-list-3-fill', label: 'Insurance Claim Specialists', sub: 'Full adjuster coordination' },
  { icon: 'ri-home-4-fill', label: 'Residential & Commercial', sub: 'All property types served' },
  { icon: 'ri-map-pin-2-fill', label: 'Gulf Coast Local Experts', sub: 'Built for coastal conditions' },
];

export default function SectionAIBlock({ eyebrow, heading, headingAccent, paragraphs, credentials }: SectionAIBlockProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const creds = credentials || DEFAULT_CREDENTIALS;

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-deep-dark relative overflow-hidden">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 opacity-4"
        style={{ backgroundImage: 'repeating-linear-gradient(45deg, #C9A961 0, #C9A961 1px, transparent 0, transparent 50%)', backgroundSize: '28px 28px' }}>
      </div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/25 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/12 to-transparent"></div>
      {/* Left gold accent bar */}
      <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/10 to-transparent hidden xl:block" style={{ left: '3rem' }}></div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left — content */}
          <div className={`flex-1 transition-all duration-800 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
            style={{ transitionDuration: '800ms' }}>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-gold"></span>
              <span className="text-[11px] font-bold tracking-super uppercase text-gold">{eyebrow || 'Roofing Services in Your Area'}</span>
            </div>
            <h2 className="font-condensed font-bold text-5xl md:text-6xl text-white uppercase leading-none mb-3">
              {heading}
              {headingAccent && (
                <>
                  <br />
                  <span className="font-serif font-normal italic text-gold text-4xl md:text-5xl normal-case leading-tight">
                    {headingAccent}
                  </span>
                </>
              )}
            </h2>
            <div className="w-10 h-px bg-gold/40 my-6"></div>
            {paragraphs.map((p, i) => (
              <p key={i} className="text-white/52 text-base leading-relaxed mb-5 font-light max-w-xl"
                dangerouslySetInnerHTML={{ __html: p }}></p>
            ))}
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <a href="#estimate" onClick={e => { e.preventDefault(); document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-3 bg-gold text-ink font-bold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-calendar-check-line"></i>Request a Free Estimate
              </a>
              <a href="tel:2285473887"
                className="inline-flex items-center gap-3 border border-white/12 text-white/65 font-semibold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:border-gold/35 hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-gold"></i>(228) 547-3887
              </a>
            </div>
          </div>

          {/* Right — credential cards */}
          <div className={`w-full lg:w-[400px] shrink-0 transition-all duration-800 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
            style={{ transitionDuration: '800ms', transitionDelay: '200ms' }}>
            <div className="grid grid-cols-1 gap-2.5">
              {creds.map((c, i) => (
                <div key={i} className="flex items-start gap-4 bg-white/3 border border-white/7 rounded-lg p-4 hover:border-gold/22 transition-colors group">
                  <div className="w-9 h-9 flex items-center justify-center shrink-0 border border-gold/28 rounded group-hover:bg-gold/8 transition-colors">
                    <i className={`${c.icon} text-gold text-sm`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">{c.label}</p>
                    <p className="text-white/30 text-xs mt-0.5 tracking-wider">{c.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
